import type { AxiosInstance } from 'axios';
import type { GetPresignedUrlRequest, UploadPresignedUrlResponse, AudioConvertResponse, GetAttachmentByIdRequest, AttachmentByIdResponse } from './contracts/assets';

/**
 * Gateway for interacting with the Assets API.
 */
export class AssetsGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}
  /**
   * Retrieves a presigned URL to upload a file to the Assets API.
   * @param params - The configuration options and parameters for the presigned URL.
   * @returns The presigned URL details wrapped in a {@link UploadPresignedUrlResponse}.
   */
  async getPresignedUrl(params: GetPresignedUrlRequest): Promise<UploadPresignedUrlResponse> {
    const { data } = await this.httpClient.get<UploadPresignedUrlResponse>(`${this.baseUrl}/uploads/presigned-url`, {
      params,
    });
    return data;
  }

  /**
   * Converts an audio file to M4A format via the Assets API.
   * @param params - The audio file to convert.
   * @param params.file - The audio file to upload for conversion.
   * @returns The converted audio response with base64-encoded data, mimetype, extension and filename.
   */
  async convertAudioToM4A(formData: any): Promise<AudioConvertResponse> {
    const { data } = await this.httpClient.post<AudioConvertResponse>(
      `${this.baseUrl}/whatsapp/audio-convert`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return data;
  }

  /**
   * Fetches an attachment by its file ID or storage key.
   *
   * When `key` is provided, the path is built from the key directly and no
   * `x-bucket-provider` header is sent. Otherwise, `fileId` and `provider` are
   * used to build the path and the provider header respectively.
   *
   * @param params - The parameters identifying the attachment.
   * @returns A signed URL to access the attachment.
   */
  async getAttachmentById(params: GetAttachmentByIdRequest): Promise<AttachmentByIdResponse> {
    const path = params.key
      ? `/uploads/${encodeURIComponent(params.key)}`
      : `/uploads/${encodeURIComponent(params.fileId!)}`;

    const axiosParams: Record<string, string> = {};

    if (params.download) axiosParams.download = 'true';
    if (params.filename) axiosParams.filename = params.filename;
    const headers: Record<string, string> = {};
    if (!params.key && params.provider) headers['x-bucket-provider'] = params.provider!;

    const { data } = await this.httpClient.get<AttachmentByIdResponse>(`${this.baseUrl}${path}`, {
      params: axiosParams,
      headers,
    });
    return data;
  }
}

export type {
  GetPresignedUrlRequest,
  UploadPresignedUrlResponse,
  IUploadPresignedUrlResponse,
  AudioConvertResponse,
  GetAttachmentByIdRequest,
  AttachmentByIdResponse,
} from './contracts/assets';
